import React, { useState, useCallback } from 'react';
import { Upload, X, Music, Image as ImageIcon, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DragDropUpload = ({ 
  type = 'image', // 'image' or 'audio'
  value = '',
  onChange,
  label = 'Upload File'
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value);

  const acceptTypes = type === 'image' 
    ? 'image/jpeg,image/png,image/gif,image/webp'
    : 'audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/aac,audio/m4a';

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const uploadFile = async (file) => {
    if (!file) return;

    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const validAudioTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/aac', 'audio/m4a', 'audio/x-m4a'];
    const validTypes = type === 'image' ? validImageTypes : validAudioTypes;

    if (!validTypes.includes(file.type)) {
      toast.error(`Invalid file type. Please upload ${type === 'image' ? 'an image' : 'an audio file'}.`);
      return;
    }

    // Check file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 50MB.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/file`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fileUrl = process.env.REACT_APP_BACKEND_URL + response.data.url;
      setPreview(fileUrl);
      onChange(fileUrl);
      toast.success(`${type === 'image' ? 'Image' : 'Audio'} uploaded successfully!`);
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      uploadFile(files[0]);
    }
  }, [type]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };

  const clearFile = () => {
    setPreview('');
    onChange('');
  };

  const Icon = type === 'image' ? ImageIcon : Music;

  return (
    <div className="space-y-2">
      <label className="text-sm text-neutral-400">{label}</label>
      
      {/* Preview */}
      {preview && (
        <div className="relative mb-2">
          {type === 'image' ? (
            <div className="relative w-full h-32 rounded-lg overflow-hidden bg-neutral-800">
              <img src={preview} alt="Preview" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={clearFile}
                className="absolute top-2 right-2 p-1 bg-black/70 rounded-full hover:bg-black"
              >
                <X size={16} className="text-white" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-3 bg-neutral-800 rounded-lg">
              <Music size={24} className="text-[#D4AF37]" />
              <audio src={preview} controls className="flex-1 h-8" />
              <button
                type="button"
                onClick={clearFile}
                className="p-1 hover:bg-neutral-700 rounded"
              >
                <X size={16} className="text-neutral-400" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Drop Zone */}
      {!preview && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer
            ${isDragging 
              ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
              : 'border-neutral-700 hover:border-neutral-600 bg-neutral-800/50'
            }
            ${uploading ? 'pointer-events-none opacity-50' : ''}
          `}
        >
          <input
            type="file"
            accept={acceptTypes}
            onChange={handleFileSelect}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={uploading}
          />
          
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 size={32} className="text-[#D4AF37] animate-spin" />
              <p className="text-sm text-neutral-400">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDragging ? 'bg-[#D4AF37]/20' : 'bg-neutral-700'}`}>
                {isDragging ? (
                  <Upload size={24} className="text-[#D4AF37]" />
                ) : (
                  <Icon size={24} className="text-neutral-400" />
                )}
              </div>
              <p className="text-sm text-neutral-300">
                {isDragging ? 'Drop file here' : `Drag & drop ${type === 'image' ? 'an image' : 'an audio file'}`}
              </p>
              <p className="text-xs text-neutral-500">or click to browse</p>
              <p className="text-xs text-neutral-600">
                {type === 'image' ? 'JPG, PNG, GIF, WebP' : 'MP3, WAV, OGG, M4A'} (max 50MB)
              </p>
            </div>
          )}
        </div>
      )}

      {/* Manual URL input */}
      <div className="flex items-center gap-2 mt-2">
        <span className="text-xs text-neutral-500">or paste URL:</span>
        <input
          type="text"
          value={preview}
          onChange={(e) => {
            setPreview(e.target.value);
            onChange(e.target.value);
          }}
          placeholder={`https://example.com/${type === 'image' ? 'image.jpg' : 'audio.mp3'}`}
          className="flex-1 bg-neutral-800 border border-neutral-700 rounded px-2 py-1 text-xs text-neutral-300 placeholder-neutral-600"
        />
      </div>
    </div>
  );
};

export default DragDropUpload;
